import React, { useEffect, useState, useCallback, useMemo } from 'react';

import { useMutation, useQuery, useQueryCache } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import {
  accountIsGeoreferenced,
  fetchAccounts,
  patchAccountGeolocation,
  InterstellarAccount,
} from '../api';
import { AccountDetailContent } from '../components/AccountDetailContent';
import { AccountSidebar, AccountSectionId, ACCOUNT_SECTIONS } from '../components/AccountSidebar';

export const AccountDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [activeSection, setActiveSection] = useState<AccountSectionId>('resumen');
  const [placementMode, setPlacementMode] = useState(false);
  const [draftLatLng, setDraftLatLng] = useState<[number, number] | null>(null);
  const queryCache = useQueryCache();

  // Use the same query key as dashboard to leverage cache
  const {
    data: accounts = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<InterstellarAccount[], Error>('interstellar-accounts', fetchAccounts, {
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Find the account by ID from the list
  const account = useMemo(() => {
    if (!accountId) return null;
    return accounts.find((a) => a.id === Number(accountId)) ?? null;
  }, [accounts, accountId]);

  const handleBack = useCallback(() => {
    navigate('/app/phone_settings');
  }, [navigate]);

  // Reset pin placement when navigating to another account
  useEffect(() => {
    setPlacementMode(false);
    setDraftLatLng(null);
  }, [accountId]);

  const cancelPlacement = useCallback((): void => {
    setPlacementMode(false);
    setDraftLatLng(null);
  }, []);

  const handleRequestGeoreference = useCallback(
    (prefillFromAccount: boolean): void => {
      if (!account) return;
      setPlacementMode(true);
      setDraftLatLng(
        prefillFromAccount && accountIsGeoreferenced(account)
          ? [Number(account.latitude), Number(account.longitude)]
          : null
      );
    },
    [account]
  );

  const handleDraftLatLngChange = useCallback((lat: number, lng: number): void => {
    setDraftLatLng([lat, lng]);
  }, []);

  const [saveGeolocation, { isLoading: savingGeolocation }] = useMutation(
    (vars: { id: number; latitude: number; longitude: number }) =>
      patchAccountGeolocation(vars.id, {
        latitude: vars.latitude,
        longitude: vars.longitude,
      }),
    {
      onSuccess: async () => {
        cancelPlacement();
        await queryCache.invalidateQueries('interstellar-accounts');
        Toast.success('Ubicación guardada en Interstellar');
      },
      onError: (err: unknown) => {
        const data = (err as { response?: { data?: { detail?: string; message?: string } } })
          ?.response?.data;
        Toast.error(
          data?.detail ||
            data?.message ||
            'No se pudo guardar la georreferencia. Revisa PUT /account/ en Interstellar.'
        );
      },
    }
  );

  const handleSaveGeolocation = useCallback((): void => {
    if (!account || !draftLatLng) return;
    saveGeolocation({
      id: account.id,
      latitude: draftLatLng[0],
      longitude: draftLatLng[1],
    });
  }, [account, draftLatLng, saveGeolocation]);

  // Keyboard shortcuts for section navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Escape cancels pin placement first, otherwise goes back
      if (e.key === 'Escape') {
        e.preventDefault();
        if (placementMode) {
          cancelPlacement();
        } else {
          handleBack();
        }
        return;
      }

      // Number keys 1-8 for section navigation
      const tabIndex = Number(e.key) - 1;
      if (tabIndex >= 0 && tabIndex < ACCOUNT_SECTIONS.length) {
        e.preventDefault();
        setActiveSection(ACCOUNT_SECTIONS[tabIndex].id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleBack, placementMode, cancelPlacement]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-white-1.0">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary-2.0 border-t-transparent" />
          <p className="text-sm text-gray-2.0">Cargando cuenta…</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center bg-white-1.0">
        <div className="text-center">
          <div className="rounded-2xl bg-red-100/80 flex items-center justify-center w-16 h-16 mx-auto mb-4">
            <Icon name={OrbitIcons.AlertCircle} size={32} className="text-red-500" />
          </div>
          <h3 className="text-lg font-bold text-black-1.0">Error al cargar cuentas</h3>
          <p className="mt-2 text-sm text-gray-2.0">
            No se pudieron cargar las cuentas de Interstellar.
          </p>
          <div className="flex justify-center gap-3 mt-4">
            <button
              type="button"
              onClick={handleBack}
              className="rounded-lg border border-white-2.0 px-4 py-2 text-sm font-medium text-gray-2.0 transition-colors hover:bg-white-1.0"
            >
              Volver al tablero
            </button>
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-primary-2.0 px-4 py-2 text-sm font-medium text-white-0.0 transition-colors hover:bg-primary-1.0"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex h-full items-center justify-center bg-white-1.0">
        <div className="text-center">
          <div className="rounded-2xl bg-amber-100/80 flex items-center justify-center w-16 h-16 mx-auto mb-4">
            <Icon name={OrbitIcons.AlertCircle} size={32} className="text-amber-500" />
          </div>
          <h3 className="text-lg font-bold text-black-1.0">Cuenta no encontrada</h3>
          <p className="mt-2 text-sm text-gray-2.0">
            La cuenta {accountId} no existe en Interstellar.
          </p>
          <div className="flex justify-center gap-3 mt-4">
            <button
              type="button"
              onClick={handleBack}
              className="rounded-lg bg-primary-2.0 px-4 py-2 text-sm font-medium text-white-0.0 transition-colors hover:bg-primary-1.0"
            >
              Volver al tablero
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-white-1.0">
      {/* Header */}
      <header className="flex flex-shrink-0 items-center gap-4 border-b border-white-2.0 bg-primary-2.0 px-4 py-3">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white-1.0 transition-colors hover:bg-white/20 hover:text-white-0.0"
          title="Volver al tablero (Esc)"
        >
          <Icon name={OrbitIcons.ChevronLeft} size={18} />
          <span>Volver</span>
        </button>
        <div className="bg-white/25 w-px h-6" />
        <div className="flex-1 min-w-0">
          <h1 className="truncate text-lg font-bold text-white-0.0">{account.name}</h1>
          <p className="text-xs text-white-1.0">ID Interstellar · {account.id}</p>
        </div>
      </header>

      {/* Main content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <AccountSidebar
          accountName={account.name}
          phoneNumber={account.phone_number}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <main className="flex-1 min-h-0 overflow-hidden">
          <AccountDetailContent
            account={account}
            activeSection={activeSection}
            placementMode={placementMode}
            draftLatLng={draftLatLng}
            savingGeolocation={savingGeolocation}
            onRequestGeoreference={handleRequestGeoreference}
            onCancelGeolocationPlacement={cancelPlacement}
            onSaveGeolocation={handleSaveGeolocation}
            onDraftLatLngChange={handleDraftLatLngChange}
          />
        </main>
      </div>
    </div>
  );
};
